import React from 'react';

const supportedEvents = [
    {
        value: 'person_provider.status_changed',
        label: 'Cambio de estatus de expediente',
        description: 'Se envía cuando un expediente de proveedor cambia de estatus.',
    },
    {
        value: 'people.updated',
        label: 'Actualización de empresa o proveedor',
        description: 'Se envía cuando cambia cualquier campo del registro People.',
    },
];

export default class WebhookDocs extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            subscriptions: [],
            editingSubscriptionId: null,
            targetUrl: '',
            rotateSecret: false,
            generatedSecret: null,
            selectedEvents: supportedEvents.map(event => event.value),
            deliveries: [],
            statuses: [],
            loading: true,
            saving: false,
            deletingId: null,
            sendingStatusId: null,
            sendingPersonProviderId: null,
            loadingManualStatusId: null,
            activeManualStatusId: null,
            manualDossiersByStatus: {},
            manualModalOpen: false,
            manualSentPayload: null,
            manualSentDelivery: null,
            manualSentPersonProviderId: null,
            resendingId: null,
            alert: null,
        };
    }

    componentDidMount() {
        this.loadPage();
    }

    normalizeSubscriptions = data => {
        if (Array.isArray(data)) {
            return data;
        }

        return data ? [data] : [];
    };

    loadPage = async () => {
        this.setState({ loading: true });

        try {
            const [subscriptionResponse, deliveriesResponse] = await Promise.all([
                axios.get('/api/webhook-subscriptions'),
                axios.get('/api/webhook-deliveries'),
            ]);

            this.setState({
                subscriptions: this.normalizeSubscriptions(subscriptionResponse.data.data),
                deliveries: deliveriesResponse.data.data || [],
                statuses: deliveriesResponse.data.statuses || [],
                loading: false,
            });
        } catch (error) {
            this.setState({
                loading: false,
                generatedSecret: null,
                alert: {
                    type: 'danger',
                    message: this.errorMessage(error, 'No se pudo cargar la configuración de webhooks.'),
                },
            });
        }
    };

    loadDeliveries = async () => {
        try {
            const response = await axios.get('/api/webhook-deliveries');
            this.setState({
                deliveries: response.data.data || [],
                statuses: response.data.statuses || [],
            });
        } catch (error) {
            this.setState({
                generatedSecret: null,
                alert: {
                    type: 'danger',
                    message: this.errorMessage(error, 'No se pudo cargar la bitácora de webhooks.'),
                },
            });
        }
    };

    resetForm = () => {
        this.setState({
            editingSubscriptionId: null,
            targetUrl: '',
            rotateSecret: false,
            generatedSecret: null,
            selectedEvents: supportedEvents.map(event => event.value),
        });
    };

    editSubscription = subscription => {
        this.setState({
            editingSubscriptionId: subscription.id,
            targetUrl: subscription.target_url,
            rotateSecret: false,
            generatedSecret: null,
            selectedEvents: subscription.subscribed_events?.length
                ? subscription.subscribed_events
                : supportedEvents.map(event => event.value),
            alert: null,
        });
    };

    saveSubscription = async event => {
        event.preventDefault();

        this.setState({ saving: true, alert: null });

        try {
            const response = await axios.post('/api/webhook-subscriptions', {
                target_url: this.state.targetUrl,
                rotate_secret: !this.state.editingSubscriptionId || this.state.rotateSecret,
                subscribed_events: this.state.selectedEvents,
                is_active: true,
            });

            const generatedSecret = response.data.generated_secret || null;

            this.resetForm();
            await this.loadPage();
            this.setState({
                saving: false,
                generatedSecret,
                alert: {
                    type: 'success',
                    message: generatedSecret
                        ? 'Webhook guardado correctamente. Copia el secret ahora; no volverá a mostrarse.'
                        : 'Webhook guardado correctamente.',
                },
            });
        } catch (error) {
            this.setState({
                saving: false,
                generatedSecret: null,
                alert: {
                    type: 'danger',
                    message: this.errorMessage(error, 'No se pudo guardar el webhook.'),
                },
            });
        }
    };

    deleteSubscription = async subscription => {
        this.setState({ deletingId: subscription.id, alert: null });

        try {
            await axios.delete(`/api/webhook-subscriptions/${subscription.id}`);

            if (this.state.editingSubscriptionId === subscription.id) {
                this.resetForm();
            }

            await this.loadPage();
            this.setState({
                deletingId: null,
                generatedSecret: null,
                alert: {
                    type: 'success',
                    message: 'Webhook desactivado correctamente.',
                },
            });
        } catch (error) {
            this.setState({
                deletingId: null,
                generatedSecret: null,
                alert: {
                    type: 'danger',
                    message: this.errorMessage(error, 'No se pudo desactivar el webhook.'),
                },
            });
        }
    };

    loadManualStatusDossiers = async status => {
        this.setState({
            activeManualStatusId: status.id,
            loadingManualStatusId: status.id,
            manualModalOpen: true,
            manualSentPayload: null,
            manualSentDelivery: null,
            manualSentPersonProviderId: null,
            alert: null,
        });

        try {
            const response = await axios.get(`/api/webhook-deliveries/manual-status/${status.id}/dossiers`);
            this.setState(state => ({
                loadingManualStatusId: null,
                manualDossiersByStatus: {
                    ...state.manualDossiersByStatus,
                    [status.id]: response.data.data || [],
                },
            }));
        } catch (error) {
            this.setState({
                loadingManualStatusId: null,
                generatedSecret: null,
                alert: {
                    type: 'danger',
                    message: this.errorMessage(error, 'No se pudieron cargar los expedientes de este estatus.'),
                },
            });
        }
    };

    sendManualStatus = async (status, personProvider) => {
        this.setState({ sendingStatusId: status.id, sendingPersonProviderId: personProvider.id, alert: null });

        try {
            const response = await axios.post('/api/webhook-deliveries/manual-status', {
                status_id: status.id,
                person_provider_id: personProvider.id,
            });
            const delivery = (response.data.data || [])[0] || null;
            await this.loadDeliveries();
            this.setState({
                sendingStatusId: null,
                sendingPersonProviderId: null,
                generatedSecret: null,
                manualSentDelivery: delivery,
                manualSentPayload: delivery?.payload || null,
                manualSentPersonProviderId: personProvider.id,
                alert: {
                    type: 'success',
                    message: `Webhook manual enviado para ${personProvider.dossier?.title || 'el expediente seleccionado'}.`,
                },
            });
        } catch (error) {
            this.setState({
                sendingStatusId: null,
                sendingPersonProviderId: null,
                generatedSecret: null,
                alert: {
                    type: 'danger',
                    message: this.errorMessage(error, 'No se pudo enviar el webhook manual.'),
                },
            });
        }
    };

    closeManualModal = () => {
        this.setState({
            manualModalOpen: false,
            manualSentPayload: null,
            manualSentDelivery: null,
            manualSentPersonProviderId: null,
            sendingStatusId: null,
            sendingPersonProviderId: null,
        });
    };

    resendDelivery = async delivery => {
        this.setState({ resendingId: delivery.id, alert: null });

        try {
            await axios.post(`/api/webhook-deliveries/${delivery.id}/resend`);
            await this.loadDeliveries();
            this.setState({
                resendingId: null,
                generatedSecret: null,
                alert: {
                    type: 'success',
                    message: `Webhook ${delivery.event_id} reenviado.`,
                },
            });
        } catch (error) {
            this.setState({
                resendingId: null,
                generatedSecret: null,
                alert: {
                    type: 'danger',
                    message: this.errorMessage(error, 'No se pudo reenviar el webhook.'),
                },
            });
        }
    };

    toggleEvent = eventValue => {
        this.setState(state => {
            const exists = state.selectedEvents.includes(eventValue);
            const selectedEvents = exists
                ? state.selectedEvents.filter(value => value !== eventValue)
                : [...state.selectedEvents, eventValue];

            return { selectedEvents };
        });
    };

    regenerateSecret = () => {
        this.setState({ rotateSecret: true, generatedSecret: null });
    };

    errorMessage = (error, fallback) => {
        const errors = error?.response?.data?.errors;

        if (errors) {
            return Object.values(errors).flat().join(' ');
        }

        return error?.response?.data?.message || fallback;
    };

    statusBadgeClass = status => {
        if (status === 'success') {
            return 'badge-success';
        }

        if (status === 'failed') {
            return 'badge-danger';
        }

        if (status === 'processing') {
            return 'badge-warning';
        }

        return 'badge-secondary';
    };

    formatPayload = payload => {
        if (!payload) {
            return null;
        }

        try {
            return JSON.stringify(payload, null, 2);
        } catch (error) {
            return String(payload);
        }
    };

    renderAlert = () => {
        if (!this.state.alert) {
            return null;
        }

        return (
            <div className={`alert alert-${this.state.alert.type} mb-6`}>
                <div>{this.state.alert.message}</div>
                {this.state.generatedSecret && (
                    <div className="mt-4">
                        <div className="font-weight-bold mb-2">Secret de firma</div>
                        <div className="input-group">
                            <input
                                type="text"
                                className="form-control"
                                value={this.state.generatedSecret}
                                readOnly
                                onFocus={event => event.target.select()}
                            />
                            <div className="input-group-append">
                                <button
                                    type="button"
                                    className="btn btn-light-primary"
                                    onClick={() => navigator.clipboard?.writeText(this.state.generatedSecret)}
                                >
                                    Copiar
                                </button>
                            </div>
                        </div>
                        <small className="d-block mt-2">
                            Guarda este secret en tu aplicación receptora. Después de salir de esta vista, Exppedify no lo mostrará otra vez.
                        </small>
                    </div>
                )}
            </div>
        );
    };

    renderSubscriptionStatus = () => {
        const count = this.state.subscriptions.length;

        if (count === 0) {
            return (
                <span className="label label-light-warning label-inline">
                    Sin endpoints activos
                </span>
            );
        }

        return (
            <span className="label label-light-success label-inline">
                {count} {count === 1 ? 'endpoint activo' : 'endpoints activos'}
            </span>
        );
    };

    renderSignatureDocs = () => (
        <div className="card card-custom gutter-b">
            <div className="card-header border-0 pt-6">
                <div className="card-title">
                    <h3 className="card-label">
                        Firma del webhook
                        <span className="d-block text-muted pt-2 font-size-sm">
                            Usa el secret generado al registrar tu URL para validar que cada evento viene de Exppedify.
                        </span>
                    </h3>
                </div>
            </div>
            <div className="card-body pt-2">
                <div className="row">
                    <div className="col-lg-6 mb-5">
                        <div className="font-weight-bold mb-3">Headers enviados</div>
                        <div className="table-responsive">
                            <table className="table table-sm table-borderless mb-0">
                                <tbody>
                                    <tr>
                                        <td><code>X-Exppedify-Event</code></td>
                                        <td className="text-muted">Tipo de evento.</td>
                                    </tr>
                                    <tr>
                                        <td><code>X-Exppedify-Delivery</code></td>
                                        <td className="text-muted">ID único del envío.</td>
                                    </tr>
                                    <tr>
                                        <td><code>X-Exppedify-Timestamp</code></td>
                                        <td className="text-muted">Unix timestamp del envío.</td>
                                    </tr>
                                    <tr>
                                        <td><code>X-Exppedify-Signature</code></td>
                                        <td className="text-muted">Firma HMAC en formato <code>sha256=...</code>.</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                    <div className="col-lg-6 mb-5">
                        <div className="font-weight-bold mb-3">Cómo validar</div>
                        <ol className="pl-4 mb-4">
                            <li>Lee el body crudo de la petición, sin modificarlo ni volverlo a serializar.</li>
                            <li>Concatena <code>X-Exppedify-Timestamp + "." + rawBody</code>.</li>
                            <li>Calcula <code>HMAC SHA-256</code> usando tu secret.</li>
                            <li>Compara contra <code>X-Exppedify-Signature</code> con comparación segura.</li>
                        </ol>
                    </div>
                </div>
                <pre className="bg-light rounded p-4 mb-0" style={{ whiteSpace: 'pre-wrap' }}>{`const crypto = require('crypto');

function isValidExppedifyWebhook(rawBody, headers, secret) {
  const timestamp = headers['x-exppedify-timestamp'];
  const signature = headers['x-exppedify-signature'];
  const payload = timestamp + '.' + rawBody;
  const expected = 'sha256=' + crypto
    .createHmac('sha256', secret)
    .update(payload)
    .digest('hex');
  const expectedBuffer = Buffer.from(expected);
  const signatureBuffer = Buffer.from(signature || '');

  if (expectedBuffer.length !== signatureBuffer.length) {
    return false;
  }

  return crypto.timingSafeEqual(expectedBuffer, signatureBuffer);
}`}</pre>
            </div>
        </div>
    );

    renderConfiguration = () => {
        const { saving, selectedEvents, subscriptions, editingSubscriptionId, deletingId, rotateSecret } = this.state;

        return (
            <div className="card card-custom gutter-b">
                <div className="card-header border-0 pt-6">
                    <div className="card-title">
                        <h3 className="card-label">
                            Endpoints de webhook
                            <span className="d-block text-muted pt-2 font-size-sm">
                                Agrega una o más URLs destino. Exppedify enviará cada evento a todas las URLs activas suscritas.
                            </span>
                        </h3>
                    </div>
                    <div className="card-toolbar">
                        {this.renderSubscriptionStatus()}
                    </div>
                </div>
                <form onSubmit={this.saveSubscription}>
                    <div className="card-body pt-2">
                        <div className="form-group">
                            <label className="font-weight-bold">URL destino</label>
                            <input
                                type="url"
                                className="form-control"
                                placeholder="https://tu-app.com/webhooks/exppedify"
                                value={this.state.targetUrl}
                                onChange={event => this.setState({ targetUrl: event.target.value })}
                                required
                            />
                            <span className="form-text text-muted">
                                Puedes registrar varias URLs. Si guardas una URL existente, se actualiza su configuración.
                            </span>
                        </div>

                        <div className="form-group">
                            <label className="font-weight-bold mb-2">Secret de firma</label>
                            <div className="alert alert-secondary mb-3">
                                Exppedify generará un secret automáticamente al guardar una URL nueva y lo mostrará una sola vez.
                            </div>
                            {editingSubscriptionId && (
                                <label className="checkbox checkbox-outline checkbox-primary">
                                    <input
                                        type="checkbox"
                                        checked={rotateSecret}
                                        onChange={event => this.setState({ rotateSecret: event.target.checked, generatedSecret: null })}
                                    />
                                    <span></span>
                                    Regenerar secret al guardar cambios
                                </label>
                            )}
                            <span className="form-text text-muted">
                                Usa ese secret en tu aplicación para validar <code>X-Exppedify-Signature</code>.
                            </span>
                        </div>

                        <div className="form-group mb-0">
                            <label className="font-weight-bold">Eventos</label>
                            <div className="row">
                                {supportedEvents.map(event => (
                                    <div className="col-lg-6 mb-4" key={event.value}>
                                        <label className="checkbox checkbox-lg checkbox-outline checkbox-primary align-items-start">
                                            <input
                                                type="checkbox"
                                                checked={selectedEvents.includes(event.value)}
                                                onChange={() => this.toggleEvent(event.value)}
                                            />
                                            <span className="mr-3 mt-1"></span>
                                            <div>
                                                <div className="font-weight-bold">{event.label}</div>
                                                <code>{event.value}</code>
                                                <div className="text-muted font-size-sm mt-1">{event.description}</div>
                                            </div>
                                        </label>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                    <div className="card-footer d-flex justify-content-between flex-wrap">
                        <div>
                            <button
                                type="submit"
                                className="btn btn-primary mr-3"
                                disabled={saving || selectedEvents.length === 0}
                            >
                                {saving ? 'Guardando...' : editingSubscriptionId ? 'Guardar cambios' : 'Generar secret y agregar URL'}
                            </button>
                            <button
                                type="button"
                                className="btn btn-light-primary"
                                onClick={this.resetForm}
                                disabled={saving}
                            >
                                Limpiar
                            </button>
                        </div>
                    </div>
                </form>

                <div className="card-body border-top pt-6">
                    <div className="font-weight-bold mb-4">URLs activas</div>
                    <div className="table-responsive">
                        <table className="table table-hover mb-0">
                            <thead>
                                <tr>
                                    <th>URL</th>
                                    <th>Eventos</th>
                                    <th className="text-right">Acciones</th>
                                </tr>
                            </thead>
                            <tbody>
                                {subscriptions.map(subscription => (
                                    <tr key={subscription.id}>
                                        <td style={{ maxWidth: 360 }}>
                                            <span className="text-break">{subscription.target_url}</span>
                                        </td>
                                        <td>
                                            {(subscription.subscribed_events || []).map(event => (
                                                <div key={event}><code>{event}</code></div>
                                            ))}
                                        </td>
                                        <td className="text-right">
                                            <button
                                                type="button"
                                                className="btn btn-sm btn-light-primary mr-2"
                                                onClick={() => this.editSubscription(subscription)}
                                            >
                                                Editar
                                            </button>
                                            <button
                                                type="button"
                                                className="btn btn-sm btn-light-danger"
                                                onClick={() => this.deleteSubscription(subscription)}
                                                disabled={deletingId !== null}
                                            >
                                                {deletingId === subscription.id ? 'Desactivando...' : 'Desactivar'}
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                                {subscriptions.length === 0 && (
                                    <tr>
                                        <td colSpan="3" className="text-center text-muted py-8">
                                            Todavía no hay URLs activas.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        );
    };

    renderManualPanel = () => {
        const { statuses, sendingStatusId, subscriptions, activeManualStatusId, loadingManualStatusId } = this.state;
        const hasSubscriptions = subscriptions.length > 0;

        return (
            <div className="card card-custom gutter-b">
                <div className="card-header border-0 pt-6">
                    <div className="card-title">
                        <h3 className="card-label">
                            Enviar webhook manual
                            <span className="d-block text-muted pt-2 font-size-sm">
                                Selecciona un estatus para elegir un expediente real y enviar el webhook.
                            </span>
                        </h3>
                    </div>
                </div>
                <div className="card-body pt-2">
                    <div className="d-flex flex-wrap">
                        {statuses.map(status => (
                            <button
                                key={status.id}
                                type="button"
                                className={`btn mr-3 mb-3 ${activeManualStatusId === status.id ? 'btn-primary' : 'btn-light-primary'}`}
                                onClick={() => this.loadManualStatusDossiers(status)}
                                disabled={loadingManualStatusId !== null || sendingStatusId !== null || !hasSubscriptions}
                            >
                                {loadingManualStatusId === status.id ? 'Cargando...' : status.name}
                            </button>
                        ))}
                        {statuses.length === 0 && (
                            <span className="text-muted">No hay estatus disponibles.</span>
                        )}
                    </div>
                    {!hasSubscriptions && (
                        <div className="text-muted font-size-sm">
                            Primero agrega al menos una URL activa para poder enviar pruebas.
                        </div>
                    )}
                    {this.renderManualModal()}
                </div>
            </div>
        );
    };

    renderManualModal = () => {
        const {
            statuses,
            sendingStatusId,
            sendingPersonProviderId,
            activeManualStatusId,
            loadingManualStatusId,
            manualDossiersByStatus,
            manualModalOpen,
            manualSentPayload,
            manualSentDelivery,
            manualSentPersonProviderId,
        } = this.state;

        if (!manualModalOpen) {
            return null;
        }

        const activeStatus = statuses.find(status => status.id === activeManualStatusId);
        const manualDossiers = activeManualStatusId ? (manualDossiersByStatus[activeManualStatusId] || []) : [];
        const sentPayload = this.formatPayload(manualSentPayload);

        return (
            <>
                <div className="modal fade show d-block" tabIndex="-1" role="dialog" style={{ backgroundColor: 'rgba(0, 0, 0, 0.45)' }}>
                    <div className="modal-dialog modal-xl modal-dialog-scrollable" role="document">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">
                                    Expedientes en {activeStatus?.name || 'estatus seleccionado'}
                                </h5>
                                <button type="button" className="close" onClick={this.closeManualModal}>
                                    <span>&times;</span>
                                </button>
                            </div>
                            <div className="modal-body">
                                <div className="table-responsive">
                                    <table className="table table-sm table-hover mb-0">
                                        <thead>
                                            <tr>
                                                <th>Expediente</th>
                                                <th>Proveedor</th>
                                                <th>RFC</th>
                                                <th className="text-right">Acciones</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {manualDossiers.map(personProvider => {
                                                const showSentPayload = sentPayload && manualSentPersonProviderId === personProvider.id;

                                                return (
                                                    <React.Fragment key={personProvider.id}>
                                                        <tr>
                                                            <td>
                                                                <div className="font-weight-bold">{personProvider.dossier?.title || '-'}</div>
                                                                <small className="text-muted">#{personProvider.id}</small>
                                                            </td>
                                                            <td>{personProvider.provider?.fiscal_name || '-'}</td>
                                                            <td><code>{personProvider.provider?.rfc || '-'}</code></td>
                                                            <td className="text-right">
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-sm btn-light-primary"
                                                                    onClick={() => this.sendManualStatus(activeStatus, personProvider)}
                                                                    disabled={sendingStatusId !== null}
                                                                >
                                                                    {sendingPersonProviderId === personProvider.id ? 'Enviando...' : 'Enviar'}
                                                                </button>
                                                            </td>
                                                        </tr>
                                                        {showSentPayload && (
                                                            <tr>
                                                                <td colSpan="4" className="pt-0">
                                                                    <div className="border rounded p-3 bg-light">
                                                                        <div className="font-weight-bold mb-2">Payload enviado</div>
                                                                        {manualSentDelivery && (
                                                                            <div className="text-muted font-size-sm mb-2">
                                                                                Entrega #{manualSentDelivery.id} · {manualSentDelivery.event_id}
                                                                            </div>
                                                                        )}
                                                                        <pre
                                                                            className="mb-0 text-break"
                                                                            style={{ whiteSpace: 'pre-wrap', maxHeight: 320, overflow: 'auto' }}
                                                                        >{sentPayload}</pre>
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                        )}
                                                    </React.Fragment>
                                                );
                                            })}
                                            {loadingManualStatusId === activeManualStatusId && (
                                                <tr>
                                                    <td colSpan="4" className="text-center text-muted py-6">
                                                        Cargando expedientes...
                                                    </td>
                                                </tr>
                                            )}
                                            {loadingManualStatusId !== activeManualStatusId && manualDossiers.length === 0 && (
                                                <tr>
                                                    <td colSpan="4" className="text-center text-muted py-6">
                                                        No hay expedientes en este estatus.
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-light-primary" onClick={this.closeManualModal}>
                                    Cerrar
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </>
        );
    };

    renderDeliveries = () => {
        const { deliveries, loading, resendingId } = this.state;

        return (
            <div className="card card-custom">
                <div className="card-header border-0 pt-6">
                    <div className="card-title">
                        <h3 className="card-label">
                            Webhooks enviados
                            <span className="d-block text-muted pt-2 font-size-sm">
                                Últimos 50 envíos registrados para tu empresa.
                            </span>
                        </h3>
                    </div>
                    <div className="card-toolbar">
                        <button
                            type="button"
                            className="btn btn-light-primary"
                            onClick={this.loadDeliveries}
                            disabled={loading}
                        >
                            Actualizar
                        </button>
                    </div>
                </div>
                <div className="card-body pt-2">
                    <div className="table-responsive">
                        <table className="table table-hover">
                            <thead>
                                <tr>
                                    <th>ID</th>
                                    <th>Evento</th>
                                    <th>Estatus</th>
                                    <th>HTTP</th>
                                    <th>Destino</th>
                                    <th>Payload enviado</th>
                                    <th>Fecha</th>
                                    <th className="text-right">Acciones</th>
                                </tr>
                            </thead>
                            <tbody>
                                {deliveries.map(delivery => {
                                    const payload = this.formatPayload(delivery.payload);

                                    return (
                                        <tr key={delivery.id}>
                                            <td>
                                                <div className="font-weight-bold">#{delivery.id}</div>
                                                <small className="text-muted">{delivery.event_id}</small>
                                            </td>
                                            <td><code>{delivery.event_type}</code></td>
                                            <td>
                                                <span className={`badge ${this.statusBadgeClass(delivery.status)}`}>
                                                    {delivery.status}
                                                </span>
                                            </td>
                                            <td>{delivery.response_status || '-'}</td>
                                            <td style={{ maxWidth: 260 }}>
                                                <span className="text-muted text-break">{delivery.target_url}</span>
                                            </td>
                                            <td style={{ minWidth: 320, maxWidth: 520 }}>
                                                {payload ? (
                                                    <pre
                                                        className="bg-light rounded p-3 mb-0 text-break"
                                                        style={{ whiteSpace: 'pre-wrap', maxHeight: 260, overflow: 'auto' }}
                                                    >{payload}</pre>
                                                ) : (
                                                    <span className="text-muted">Sin payload registrado.</span>
                                                )}
                                            </td>
                                            <td>{delivery.created_at}</td>
                                            <td className="text-right">
                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-light-primary"
                                                    onClick={() => this.resendDelivery(delivery)}
                                                    disabled={resendingId !== null}
                                                >
                                                    {resendingId === delivery.id ? 'Reenviando...' : 'Reenviar'}
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })}
                                {!loading && deliveries.length === 0 && (
                                    <tr>
                                        <td colSpan="8" className="text-center text-muted py-8">
                                            Todavía no hay webhooks enviados.
                                        </td>
                                    </tr>
                                )}
                                {loading && (
                                    <tr>
                                        <td colSpan="8" className="text-center text-muted py-8">
                                            Cargando webhooks...
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        );
    };

    render() {
        return (
            <>
                {this.renderAlert()}
                {this.renderSignatureDocs()}
                {this.renderConfiguration()}
                {this.renderManualPanel()}
                {this.renderDeliveries()}
            </>
        );
    }
}
